import React, { useEffect, useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'shared/assets/icons';
import { Datepicker } from 'shared/components/ui/datepicker';
import { FormInput, Sizes } from 'shared/components/ui/form-input';
import { PopupMenu, PopupMenuList } from 'shared/components/ui/popup-menu';
import { useDateInput } from 'shared/hooks/use-date-input';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';
import { parseDateInput } from 'shared/utils/parse-date-input';

import * as Styled from './form-date-input.style';

export type FormDateInputProps = Omit<
  React.ComponentProps<typeof Datepicker>,
  | 'onChange'
  | 'selected'
  | 'customInput'
  | 'popperProps'
  | 'wrapperClassName'
  | 'weekStartsOn'
  | 'placeholderText'
> & {
  control: Control<any>;
  name: string;
  error?: string;
  size?: Sizes;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  placeholderText?: string;
  minDate?: Date;
  maxDate?: Date;
  today: Date;
  canClear?: boolean;
};

type DateInputFieldProps = Omit<FormDateInputProps, 'control' | 'name'> & {
  value: Date | null;
  onChange: (date: Date | null) => void;
};

const DateInputField: React.FC<DateInputFieldProps> = ({
  value,
  onChange,
  error,
  size,
  dateFormat,
  weekStartsOn,
  placeholderText,
  today,
  canClear = true,
  ...datepickerProps
}) => {
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const ref = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const {
    value: text,
    onChange: onMaskedChange,
    onKeyDown,
    reset: resetText,
  } = useDateInput(value ?? undefined, dateFormat);
  const [hasError, setHasError] = useState(false);

  const { minDate, maxDate } = datepickerProps;

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = onMaskedChange(event);
    setHasError(false);

    if (!formatted) {
      if (canClear) {
        onChange(null);
      }
      return;
    }

    const parsed = parseDateInput(formatted, dateFormat, today, {
      minDate,
      maxDate,
    });
    if (parsed) {
      onChange(parsed);
    }
  };

  const onFocusInput = () => {
    setHasError(false);
    openMenu();
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    // Focus dropping to nowhere (clicking the field to reposition the cursor closes
    // the inline calendar and momentarily drops focus to the body) or moving into the
    // calendar popup is not a real exit — only validate once focus lands on another
    // focusable element outside this control.
    if (!nextTarget || popupRef.current?.contains(nextTarget)) {
      return;
    }

    closeMenu();

    if (!text) {
      if (!canClear) {
        resetText(value ?? undefined);
      }
      return;
    }

    const parsed = parseDateInput(text, dateFormat, today, {
      minDate,
      maxDate,
    });
    setHasError(!parsed);
  };

  const onClear = () => {
    resetText();
    setHasError(false);
    onChange(null);
  };

  const onSelectDate = (date: Date | [Date | null, Date | null] | null) => {
    const selected = Array.isArray(date) ? date[0] : date;
    setHasError(false);
    onChange(selected);
    closeMenu();
  };

  const errorMessage = hasError ? t('forms.fields.date.invalid') : error;

  useEffect(() => {
    resetText(value ?? undefined);
  }, [value, resetText]);

  return (
    <>
      <FormInput
        ref={ref}
        value={text}
        onChange={onChangeInput}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onFocus={onFocusInput}
        onClick={openMenu}
        iconRight={Calendar}
        error={errorMessage}
        canClear={canClear}
        onClear={onClear}
        size={size}
        placeholder={placeholderText}
        autoComplete="off"
      />
      {hasError && (
        <Styled.Error>{t('forms.fields.date.invalid')}</Styled.Error>
      )}
      {menuOpen && (
        <PopupMenu
          ref={popupRef}
          referenceElement={ref}
          onOutside={closeMenu}
          noBackdrop
          preventReferenceElementOverlap
        >
          <PopupMenuList>
            <Datepicker
              wrapperClassName={Styled.datePickerWrapperClassName}
              onChange={onSelectDate}
              selected={value}
              inline
              weekStartsOn={weekStartsOn}
              today={today}
              {...datepickerProps}
            />
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};

export const FormDateInput: React.FC<FormDateInputProps> = ({
  control,
  name,
  ...rest
}) => (
  <Styled.Container>
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <DateInputField
          {...rest}
          value={field.value ?? null}
          onChange={field.onChange}
        />
      )}
    />
  </Styled.Container>
);
